import { forwardRef, Ref, useMemo } from "react";
import cx from "classnames";

import { SrOnly } from "../../../../../../components/SrOnly";

import { MultiSelectMenuSync } from "../../../../../../beta/components/MultiSelectMenu/MultiSelectMenuSync";
import { MultiSelectMenuOption } from "../../../../../../beta/components/MultiSelectMenu/types";

import { DataTableEditableCellEditIcon } from "./DataTableEditableCellEditIcon";
import { TableBodyCell } from "../../../base/cells/TableBodyCell";

import styles from "./DataTableEditableCells.module.scss";
import { EditableCellImperativeHandle } from "./types";
import { formatCellPositionString } from "../util/cellPositionHelpers";
import { mergeRefs } from "../../../../../../hooks/useMergeRefs/useMergeRefs";
import { BaseEditableCellProps } from "./types";
import {
  getColumnId,
  getCellValue,
  logWarningForInvalidCellValueType,
} from "../util/cellTypeHelpers";
import { DataTableBodyImmutableCell } from "../cells/DataTableBodyImmutableCell";
import { CellFocusContext } from "../cells/CellFocusContext";
import { useEditableMenuCell } from "./useEditableMenuCell";

export type DataTableEditableMultiselectCellProps<T> = BaseEditableCellProps<T>;

/**
 * Renders an editable multiselect cell in a DataTable. This component provides a
 * MultiSelectMenu popover with search that appears when the cell is activated, allowing
 * users to select multiple values from a list of options. Supports keyboard navigation
 * and live selection.
 *
 * @param props - The props for the DataTableEditableMultiselectCell component
 * @param props.cell - The TanStack table cell instance
 * @param props.cellProps - Props for styling and event handling of the cell container
 * @param props.cellPosition - The position of the cell in the table
 * @param props.children - The content to display when not editing
 * @param props.isHovered - Whether the cell is currently hovered
 * @param ref - Ref to the imperative handle for programmatic control
 * @returns The rendered editable multiselect cell component
 */
export function DataTableEditableMultiselectCellInner<T>(
  props: DataTableEditableMultiselectCellProps<T>,
  ref: React.Ref<EditableCellImperativeHandle>,
) {
  const { cell, cellProps, cellPosition, children } = props;
  const { onKeyDown: onCellKeyDown, className, ...restCellProps } = cellProps;

  const columnId = getColumnId(cell);
  const cellValue = getCellValue(cell, columnId);

  const isValidValueType = useMemo(() => {
    if (cellValue === null || cellValue === undefined) return true;
    if (Array.isArray(cellValue)) return true;
    logWarningForInvalidCellValueType({
      columnId: String(columnId),
      editMode: "multiselect",
      expectedType: "array of primitive values",
      receivedType: typeof cellValue,
    });
    return false;
  }, [cellValue, columnId]);

  const meta = cell.column.columnDef.meta;
  const editConfig = meta?.editConfig;
  const multiselectEditConfig =
    editConfig?.mode === "multiselect" ? editConfig : undefined;

  const options: MultiSelectMenuOption[] = useMemo(
    () => multiselectEditConfig?.options ?? [],
    [multiselectEditConfig],
  );

  const onChange = multiselectEditConfig?.onChange;

  const {
    mode: _mode,
    options: _options,
    onChange: _onChange,
    ...multiSelectMenuPassthroughProps
  } = multiselectEditConfig ?? {};

  const rowId = cell.row.id;

  const {
    tabIndex,
    cellRef,
    triggerClickRef,
    handleCellKeyDown,
    handleMenuKeyDown,
    handleImplicitClose,
    handleCellMouseDown,
    handleCellBlur,
    handleCellFocus,
    cellFocusContextValue,
  } = useEditableMenuCell({ cellPosition, onCellKeyDown, ref });

  // Determine currently selected options from cell value (array of ids)
  const selectedOptions = useMemo(() => {
    if (!Array.isArray(cellValue)) return [];
    const valueStrings = cellValue.map((v) => String(v));
    return options.filter((o) => valueStrings.includes(String(o.id)));
  }, [options, cellValue]);

  const hasEditableConfig = !!multiselectEditConfig;

  if (!isValidValueType || !hasEditableConfig) {
    const { cellProps: _cellProps, ...immutableCellProps } = props;
    return <DataTableBodyImmutableCell {...immutableCellProps} />;
  }

  return (
    <MultiSelectMenuSync
      trigger={({
        ref: triggerRef,
        onClick,
        onKeyDown: menuKeyDown,
        "aria-controls": ariaControls,
        "aria-expanded": ariaExpanded,
        "aria-haspopup": ariaHaspopup,
        "data-state": dataState,
      }) => {
        triggerClickRef.current = onClick;
        const isOpen = ariaExpanded;
        return (
          <TableBodyCell
            ref={mergeRefs([
              triggerRef as Ref<HTMLElement>,
              cellRef as Ref<HTMLElement>,
            ])}
            tabIndex={tabIndex}
            type="data-table"
            {...restCellProps}
            className={cx(styles["data-table-body-cell-editable"], className)}
            onMouseDown={handleCellMouseDown(onClick)}
            onBlur={handleCellBlur}
            onFocus={handleCellFocus}
            data-cell-pos={formatCellPositionString(cellPosition)}
            aria-controls={ariaControls}
            aria-expanded={ariaExpanded}
            aria-haspopup={ariaHaspopup}
            data-state={dataState}
            onKeyDown={(e) => handleCellKeyDown(e, menuKeyDown, isOpen)}
          >
            <CellFocusContext.Provider value={cellFocusContextValue}>
              {children}
              {!isOpen && <DataTableEditableCellEditIcon />}
            </CellFocusContext.Provider>
            <SrOnly>
              {isOpen
                ? "Press Enter or Space to select cell values. Press Escape or F2 to close."
                : "Press Enter or F2 to edit cell value."}
            </SrOnly>
          </TableBodyCell>
        );
      }}
      label={String(
        cell.column.columnDef.meta?.columnDef?.headerLabel ?? "Select",
      )}
      options={options}
      value={selectedOptions}
      onSelectedOptionsChange={(selected) => {
        onChange?.(selected, rowId);
      }}
      onMenuKeyDown={handleMenuKeyDown}
      onImplicitClose={handleImplicitClose}
      {...multiSelectMenuPassthroughProps}
    />
  );
}

export const DataTableEditableMultiselectCell = Object.assign(
  forwardRef(DataTableEditableMultiselectCellInner) as <T>(
    props: DataTableEditableMultiselectCellProps<T> & {
      ref?: React.Ref<EditableCellImperativeHandle>;
    },
  ) => React.ReactElement,
  { displayName: "DataTableEditableMultiselectCell" },
);
